import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showToast } from '@repo/ui';
import { tenantSettingsApi } from '../api/tenantSettingsApi';
import { tenantQueryKeys } from './useTenantQuery';

/**
 * Hook to save reporting configuration
 */
export function useReportingConfigMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      enabled: boolean;
      dataProcessingStartDate?: string;
      arExceptionReporting: {
        enabled: boolean;
      };
      trustReconciliationReporting: {
        enabled: boolean;
        liabilityAccountId?: string;
        trustAccounts: Array<{
          clioTrustAccountId: string;
          qboTrustAccountId: string;
          trustAccountName: string;
          trustAccountDescription?: string;
        }>;
      };
      matterLedgerReporting: {
        enabled: boolean;
        pdfTemplateId?: string;
      };
      matterStatementReporting: {
        enabled: boolean;
        pdfTemplateId?: string;
      };
    }) => {
      const currentConfig = await tenantSettingsApi.getCurrentTenantConfig();

      const apiPayload = {
        modules: {
          ...currentConfig.modules,
          reporting: {
            enabled: data.enabled,
            dataProcessingStartDate: data.dataProcessingStartDate || '2024-01-01',
            arExceptionReporting: {
              enabled: data.arExceptionReporting.enabled,
            },
            trustReconciliationReporting: {
              enabled: data.trustReconciliationReporting.enabled,
              liabilityAccountId: data.trustReconciliationReporting.liabilityAccountId || undefined,
              trustAccounts: data.trustReconciliationReporting.trustAccounts,
            },
            matterLedgerReporting: {
              enabled: data.matterLedgerReporting.enabled,
              pdfTemplateId: data.matterLedgerReporting.pdfTemplateId || undefined,
            },
            matterStatementReporting: {
              enabled: data.matterStatementReporting.enabled,
              pdfTemplateId: data.matterStatementReporting.pdfTemplateId || undefined,
            },
            healthCheckEndpoint: '/health/reporting',
          },
        },
      };

      return await tenantSettingsApi.updateCurrentTenantConfig(apiPayload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantQueryKeys.config });
      queryClient.invalidateQueries({ queryKey: tenantQueryKeys.configurationStatus });
      showToast.success('Reporting configuration saved successfully!');
    },
    onError: error => {
      console.error('Reporting config save error:', error);
      showToast.error('Failed to save reporting configuration');
    },
  });
}

